import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BizzCustomersService } from 'src/app/_business-dashboard/_services/bizz-customers.service';
import { Status } from 'src/app/_models/coupon-management';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { BusinessSetupService, OnlineStoreSetupStatus } from 'src/app/_services/business-setup.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { CurrencySettingsService } from 'src/app/_services/currency-settings.service';
import { GlobalService } from 'src/app/_services/global.service';

interface TimezoneOption {
  label: string;
  value: string;      // Windows tzName (e.g. "Central Standard Time") — matches CountryTimeZone.TimeZones.TZName,
                       // used as-is for both business/timezone GET (preselect) and POST (save).
  utcOffset: string;   // e.g. "-06:00"
  countryCode: string;
}

interface TimezoneGroup {
  label: string;
  items: TimezoneOption[];
}

// Values/labels match the backend `StoreContentStatus` enum in account-svc
// (InProgress = 0, PendingByClient = 1, Completed = 2, Other = 3).
const STORE_CONTENT_STATUS_LABELS: { [key: number]: string } = {
  0: 'Pending from user',
  1: 'Details submitted',
  2: 'Completed',
  3: 'Other',
};

// "Other" (3) is intentionally excluded — not a selectable status from this admin dropdown.
const STORE_CONTENT_STATUS_OPTIONS: { label: string; value: number }[] = [
  { label: 'Pending from user', value: 0 },
  { label: 'Details submitted', value: 1 },
  { label: 'Completed', value: 2 },
];

// Mirrors the merchant-facing storeTemplates list in online-store-setup.component.ts (name/image
// per OnlineStoreTemplateId) so admin can see the same preview the merchant picked.
const ONLINE_STORE_TEMPLATES: { [templateId: number]: { name: string; image: string } } = {
  1: { name: 'Trendovo', image: 'assets/images/store-templates/tpl-trendovo.webp' },
  2: { name: 'Luminest', image: 'assets/images/store-templates/tpl-luminest.webp' },
  3: { name: 'Timelore', image: 'assets/images/store-templates/tpl-timelore.webp' },
};

// Mirrors the merchant-facing brandColorOptions list in online-store-setup.component.ts (code ->
// display name/hex) so the admin panel can render the same swatches as chips.
const BRAND_COLOR_OPTIONS: { [code: string]: { name: string; hex: string } } = {
  blue: { name: 'Blue', hex: '#2563EB' },
  navy: { name: 'Navy', hex: '#1E3A8A' },
  teal: { name: 'Teal', hex: '#0D9488' },
  cyan: { name: 'Cyan', hex: '#06B6D4' },
  sky_blue: { name: 'Sky Blue', hex: '#0EA5E9' },
  royal_blue: { name: 'Royal Blue', hex: '#1D4ED8' },
  indigo: { name: 'Indigo', hex: '#4F46E5' },
  violet: { name: 'Violet', hex: '#7C3AED' },
  orange: { name: 'Orange', hex: '#F97316' },
  amber: { name: 'Amber', hex: '#F59E0B' },
  yellow: { name: 'Yellow', hex: '#EAB308' },
  gold: { name: 'Gold', hex: '#CA8A04' },
  green: { name: 'Green', hex: '#22C55E' },
  emerald: { name: 'Emerald', hex: '#10B981' },
  lime: { name: 'Lime', hex: '#84CC16' },
  purple: { name: 'Purple', hex: '#9333EA' },
  magenta: { name: 'Magenta', hex: '#D946EF' },
  pink: { name: 'Pink', hex: '#EC4899' },
  red: { name: 'Red', hex: '#EF4444' },
  coral: { name: 'Coral', hex: '#FB7185' },
  brown: { name: 'Brown', hex: '#8B5E3C' },
  white: { name: 'White', hex: '#FFFFFF' },
  gray: { name: 'Gray', hex: '#6B7280' },
  black: { name: 'Black', hex: '#111827' },
};

@Component({
  selector: 'app-bizz-customer-personal-info',
  templateUrl: './bizz-customer-personal-info.component.html',
  styleUrls: ['./bizz-customer-personal-info.component.scss']
})
export class BizzCustomerPersonalInfoComponent implements OnInit, OnDestroy {

  isInfoExpanded: boolean = false;

  statusList: any[] = [
    { id: 0, viewValue: 'Active', value: true },
    { id: 1, viewValue: 'Inactive', value: false },
  ];

  statusValue: FormControl = new FormControl()
  initialStatusValue: boolean = true;
  isStatusDirty: boolean = false;
  isChangeStatus: boolean = false;
  isSavingStatus: boolean = false;
  statusReason: string = '';
  stateOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ]
  showCurrencyDropdown = false;
  currencyOptions: Array<{ label: string; value: string; name?: string; symbol?: string; raw?: any }> = [
    { label: 'US ($)', value: 'USD', name: 'US Dollar', symbol: '$' },
    { label: 'Pakistan Rupees (PKR)', value: 'PKR', name: 'Pakistan Rupees', symbol: 'PKR' },
  ];
  selectedCurrency: string = 'USD';
  initialCurrency: string = 'USD';
  isCurrencyDirty: boolean = false;
  isSavingCurrency: boolean = false;

  // Options come from account-svc GetTimeZonesApi, which flattens every IsActive CountryTimeZone
  // doc's embedded TimeZones[] into one list (currently ctz-us + ctz-pk) — grouped here by country
  // so more countries can be added later just by seeding another active CountryTimeZone doc, no
  // frontend change needed. One timezone is selected at a time (single-select). Labels are
  // prefixed with the zone's UTC offset and the dropdown shows a running clock for the selected
  // zone — the same cues Windows' Settings > Date & time > Time zone picker gives, so admins can
  // sanity-check the pick.
  // Falls back to US Central when the customer has no timezone on file yet, rather than leaving
  // the dropdown blank.
  private static readonly DEFAULT_TIMEZONE = 'Central Standard Time';
  showTimezoneDropdown = true;
  timezoneGroups: TimezoneGroup[] = [];
  selectedTimezone: string | null = BizzCustomerPersonalInfoComponent.DEFAULT_TIMEZONE;
  initialTimezone: string | null = BizzCustomerPersonalInfoComponent.DEFAULT_TIMEZONE;
  isTimezoneDirty: boolean = false;
  isSavingTimezone: boolean = false;
  now: Date = new Date();
  private clockHandle: any = null;
  // The customer-detail payload (adminaccountdetail report, already loaded to render this whole
  // panel) embeds the business's saved zone directly at data.activeTimeZone — same shape as a
  // business/timezoneoptions entry ({ tzName, utcOffset, countryCode, isActive }). That's the
  // authoritative, already-available source for what's selected; the separate
  // business/timezoneoptions call's inline isActive flag is only a fallback if this is absent.
  private customerActiveTimezoneOption: TimezoneOption | null = null;


  constructor(
    public _bizzCustomerService: BizzCustomersService,
    private _connMessageService: ConnMessageService,
    public _abd: AppBaseDataService,
    private _confirmationService: ConfirmationService,
    private _currencySettings: CurrencySettingsService,
    private _businessSetupService: BusinessSetupService,

  ) { }

  ngOnInit(): void {
    
    // if(this._bizzCustomerService.activeItem.accountStatusString === "Account Expired" || this._bizzCustomerService.activeItem.accountStatusString === "Deployment Completed" && this._abd.isAdmin === true){
    if (this._abd.isAdmin === true) {
      this.isChangeStatus = true
      this.initialStatusValue = this.resolveInitialStatusValue();
      this.statusValue.setValue(this.initialStatusValue)
      this.statusValue.valueChanges.subscribe((value) => {
        this.isStatusDirty = !!value !== this.initialStatusValue;
      });
    } else {
      this.isChangeStatus = false
    }

    this.showCurrencyDropdown = true;
    this.selectedCurrency = this.getCurrencyCodeFromCustomerDetail();
    this.initialCurrency = this.selectedCurrency;
    if (this.showCurrencyDropdown) {
      this.loadCurrencyOptions();
    }

    this.initializeTimezoneFromCustomerDetail();
    this.loadTimezoneOptions();
    this.clockHandle = setInterval(() => { this.now = new Date(); }, 1000);
  }

  ngOnDestroy(): void {
    if (this.clockHandle) {
      clearInterval(this.clockHandle);
    }
  }

  // data.activeTimeZone on the already-loaded customer-detail payload (adminaccountdetail report)
  // is the authoritative "what's currently selected" source — set the dropdown from it immediately,
  // synchronously, so it renders correctly on first paint instead of waiting on a second API call.
  private initializeTimezoneFromCustomerDetail(): void {
    const item = this.unwrapActiveItem();
    const activeTz = item?.activeTimeZone ?? item?.ActiveTimeZone;
    const tzName = String(activeTz?.tzName ?? activeTz?.TZName ?? '').trim();
    if (!tzName) return;

    const utcOffset = String(activeTz?.utcOffset ?? activeTz?.UTCOffset ?? '').trim();
    const countryCode = String(activeTz?.countryCode ?? activeTz?.CountryCode ?? '').trim();
    const option: TimezoneOption = { value: tzName, utcOffset, countryCode, label: this.buildOffsetLabel(tzName, utcOffset) };

    this.customerActiveTimezoneOption = option;
    this.selectedTimezone = tzName;
    this.initialTimezone = tzName;
    this.isTimezoneDirty = false;
    this.timezoneGroups = this.mergeOptionIntoGroups(this.timezoneGroups, option);
  }

  private buildOffsetLabel(tzName: string, utcOffset: string): string {
    const offsetPrefix = this.formatUtcOffsetText(utcOffset);
    return offsetPrefix ? `${offsetPrefix} ${tzName}` : tzName;
  }

  // Ensures `option` is selectable in the dropdown even if the live/fallback list didn't happen to
  // include it — without this, PrimeNG would render the customer's actual saved zone as blank.
  private mergeOptionIntoGroups(groups: TimezoneGroup[], option: TimezoneOption): TimezoneGroup[] {
    if (groups.some((g) => g.items.some((i) => i.value === option.value))) {
      return groups;
    }

    const groupLabel = BizzCustomerPersonalInfoComponent.COUNTRY_NAMES[option.countryCode.toUpperCase()] || option.countryCode || 'Other';
    const next = groups.map((g) => ({ label: g.label, items: [...g.items] }));
    const existingGroup = next.find((g) => g.label === groupLabel);
    if (existingGroup) {
      existingGroup.items.push(option);
    } else {
      next.push({ label: groupLabel, items: [option] });
    }

    return next.sort((a, b) => {
      if (a.label === 'United States') return -1;
      if (b.label === 'United States') return 1;
      return a.label.localeCompare(b.label);
    });
  }

  // GetTimeZoneOptions (business/timezoneoptions) returns the selectable zones for this specific
  // business — each entry carries tzName (Windows id)/utcOffset/countryCode/isActive, sourced from
  // the CountryTimeZone container. Populates the picker's list; only used to derive the *selected*
  // zone when initializeTimezoneFromCustomerDetail() above found nothing (customer has no
  // activeTimeZone on file yet).
  private loadTimezoneOptions(): void {
    const businessName = this.getBusinessNameForCustomer();
    this._businessSetupService.getBusinessTimeZoneOptions(businessName).subscribe({
      next: (res) => {
        const zones = this.extractZones(res);
        let groups = zones.length ? this.groupZonesByCountry(zones) : this.buildFallbackTimezoneGroups();

        if (this.customerActiveTimezoneOption) {
          groups = this.mergeOptionIntoGroups(groups, this.customerActiveTimezoneOption);
        } else {
          const active = this.findActiveTimezone(zones);
          if (active) {
            this.selectedTimezone = active;
            this.initialTimezone = active;
            this.isTimezoneDirty = false;
          }
        }

        this.timezoneGroups = groups;
      },
      error: () => {
        let groups = this.buildFallbackTimezoneGroups();
        if (this.customerActiveTimezoneOption) {
          groups = this.mergeOptionIntoGroups(groups, this.customerActiveTimezoneOption);
        }
        this.timezoneGroups = groups;
      }
    });
  }

  private findActiveTimezone(zones: any[]): string | null {
    const active = zones.find((zone: any) =>
      zone?.isActive === true || zone?.IsActive === true ||
      zone?.isSelected === true || zone?.IsSelected === true ||
      zone?.isCurrent === true || zone?.IsCurrent === true
    );
    if (!active) return null;

    const tzName = active?.tzName ?? active?.TZName ?? active?.TzName ?? active?.ZoneName ?? active?.zoneName ?? null;
    return tzName ? String(tzName).trim() : null;
  }

  private extractZones(res: any): any[] {
    const data = res?.data ?? res;
    const list = data?.Zones ?? data?.zones ?? data;
    return Array.isArray(list) ? list : [];
  }

  // The live business/timezoneoptions response doesn't include a country display name — only
  // countryCode (e.g. { tzName: "Pakistan Standard Time", utcOffset: "+05:00", countryCode: "PK",
  // isActive: true }) — so group headers are resolved from the code here rather than assumed to
  // come from the API. Extend alongside FALLBACK_TIMEZONES when a new country is added.
  private static readonly COUNTRY_NAMES: { [code: string]: string } = {
    US: 'United States',
    PK: 'Pakistan',
  };

  private groupZonesByCountry(zones: any[]): TimezoneGroup[] {
    const byCountry = new Map<string, TimezoneGroup>();

    zones.forEach((zone) => {
      const tzName = String(zone?.tzName ?? zone?.TZName ?? zone?.TzName ?? zone?.ZoneName ?? zone?.zoneName ?? '').trim();
      if (!tzName) return;

      const countryCode = String(zone?.countryCode ?? zone?.CountryCode ?? '').trim();
      const countryName = String(zone?.countryName ?? zone?.CountryName ?? '').trim()
        || BizzCustomerPersonalInfoComponent.COUNTRY_NAMES[countryCode.toUpperCase()]
        || countryCode
        || 'Other';
      const utcOffset = String(zone?.utcOffset ?? zone?.UTCOffset ?? zone?.UtcOffset ?? zone?.GmtOffset ?? zone?.gmtOffset ?? '').trim();

      if (!byCountry.has(countryName)) {
        byCountry.set(countryName, { label: countryName, items: [] });
      }

      const offsetPrefix = this.formatUtcOffsetText(utcOffset);
      byCountry.get(countryName)!.items.push({
        label: offsetPrefix ? `${offsetPrefix} ${tzName}` : tzName,
        value: tzName,
        utcOffset,
        countryCode,
      });
    });

    // United States pinned first (matches the prior static ordering), rest alphabetical.
    return Array.from(byCountry.values()).sort((a, b) => {
      if (a.label === 'United States') return -1;
      if (b.label === 'United States') return 1;
      return a.label.localeCompare(b.label);
    });
  }

  // utcOffset is expected as a "+HH:MM"/"-HH:MM" string (matches the save-payload example), with a
  // seconds-based fallback in case the source ever returns it that way instead.
  private formatUtcOffsetText(raw: any): string {
    if (raw === null || raw === undefined || raw === '') return '';

    const text = String(raw).trim();
    if (!text.includes(':') && Number.isFinite(Number(text))) {
      const totalMinutes = Math.round(Number(text) / 60);
      const sign = totalMinutes >= 0 ? '+' : '-';
      const abs = Math.abs(totalMinutes);
      const hh = String(Math.floor(abs / 60)).padStart(2, '0');
      const mm = String(abs % 60).padStart(2, '0');
      return `(UTC${sign}${hh}:${mm})`;
    }

    const withSign = text.startsWith('+') || text.startsWith('-') ? `UTC${text}` : text;
    return withSign.startsWith('(') ? withSign : `(${withSign})`;
  }

  private getBusinessNameForCustomer(): string {
    const item = this.unwrapActiveItem();
    return String(item?.currentSubscription?.businessNameWithDomain ?? item?.businessNameWithDomain ?? '').trim();
  }

  // Used only if GetTimeZonesApi errors, or the CountryTimeZone container isn't seeded yet under
  // that name — keeps the picker usable (matching the real US+PK zones/offsets) instead of empty.
  private static readonly FALLBACK_TIMEZONES: Array<{ tzName: string; countryName: string; countryCode: string; utcOffset: string }> = [
    { tzName: 'SA Western Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-04:00' },
    { tzName: 'US Mountain Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-07:00' },
    { tzName: 'Alaskan Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-09:00' },
    { tzName: 'Hawaiian Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-10:00' },
    { tzName: 'Eastern Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-05:00' },
    { tzName: 'Central Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-06:00' },
    { tzName: 'Mountain Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-07:00' },
    { tzName: 'Pacific Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-08:00' },
    { tzName: 'Pakistan Standard Time', countryName: 'Pakistan', countryCode: 'PK', utcOffset: '+05:00' },
  ];

  private buildFallbackTimezoneGroups(): TimezoneGroup[] {
    const byCountry = new Map<string, TimezoneGroup>();

    BizzCustomerPersonalInfoComponent.FALLBACK_TIMEZONES.forEach((tz) => {
      if (!byCountry.has(tz.countryName)) {
        byCountry.set(tz.countryName, { label: tz.countryName, items: [] });
      }
      const offsetPrefix = this.formatUtcOffsetText(tz.utcOffset);
      byCountry.get(tz.countryName)!.items.push({
        label: offsetPrefix ? `${offsetPrefix} ${tz.tzName}` : tz.tzName,
        value: tz.tzName,
        utcOffset: tz.utcOffset,
        countryCode: tz.countryCode,
      });
    });

    return Array.from(byCountry.values()).sort((a, b) => {
      if (a.label === 'United States') return -1;
      if (b.label === 'United States') return 1;
      return a.label.localeCompare(b.label);
    });
  }

  private get selectedTimezoneOption(): TimezoneOption | null {
    for (const group of this.timezoneGroups) {
      const match = group.items.find((i) => i.value === this.selectedTimezone);
      if (match) return match;
    }
    return null;
  }

  // Live "Current time: ..." readout under the dropdown, same cue Windows shows under its time
  // zone picker so the admin can confirm they picked the zone they meant. Computed purely from the
  // zone's static UTC offset (not the IANA/Intl timezone database — Windows tzNames like "Central
  // Standard Time" aren't valid Intl timeZone identifiers), so it works for any zone the API
  // returns without needing a translation table, but doesn't reflect DST.
  get selectedTimezoneCurrentTime(): string {
    const option = this.selectedTimezoneOption;
    const offsetMinutes = option ? this.parseUtcOffsetMinutes(option.utcOffset) : null;
    if (offsetMinutes === null) return '';

    const utcMs = this.now.getTime() + this.now.getTimezoneOffset() * 60000;
    const zoneDate = new Date(utcMs + offsetMinutes * 60000);

    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC', // zoneDate's UTC fields already represent the target zone's local time
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(zoneDate);
  }

  private parseUtcOffsetMinutes(offset: string): number | null {
    const match = String(offset || '').trim().match(/^([+-])(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const sign = match[1] === '-' ? -1 : 1;
    return sign * (Number(match[2]) * 60 + Number(match[3]));
  }

  // Reason is required for any status change (Active or Inactive); required before Save.
  // TODO re-enable: set to true to show the status reason input and require it on status change
  readonly STATUS_REASON_ENABLED = false;

  get isStatusReasonRequired(): boolean {
    return this.STATUS_REASON_ENABLED && this.isStatusDirty;
  }

  saveStatus() {
    if (!this.isStatusDirty || (this.isStatusReasonRequired && !this.statusReason.trim())) {
      return;
    }

    this._confirmationService.confirm({
      message: `Do you wish to change this account status to ${this.statusValue.value === true ? "Active" : "Inactive"}?`,
      header: 'Account Status',
      icon: 'pi pi-info-circle',
      accept: () => {
        const activeItem = this._bizzCustomerService?.activeItem || {};
        const customerID =
          activeItem?.userId ??
          activeItem?.currentSubscription?.userId ??
          activeItem?.userID ??
          null;

        if (!customerID) {
          this._connMessageService.showToastMessage(
            'Customer id not found. Unable to change status.',
            'error'
          );
          return;
        }
        const businessId =
          activeItem?.businessId ??
          activeItem?.currentSubscription?.businessId ??
          activeItem?.businessSetup?.businessId ??
          null;
        const displayStatus = this.statusValue.value === true ? 'Active' : 'Inactive';

        const reason = this.isStatusReasonRequired ? this.statusReason.trim() : null;

        this.isSavingStatus = true;
        this._bizzCustomerService.updateBusinessStatus(customerID, this.statusValue.value, reason).subscribe((res) => {
          if (!res?.success) {
            this.isSavingStatus = false;
            this._connMessageService.showToastMessage('Customer status could not be changes.', 'error');
            return;
          }

          this._businessSetupService
            .saveAccountStatusDisplay(displayStatus, customerID, businessId)
            .subscribe({
              next: (displayRes) => {
                this.isSavingStatus = false;
                if (displayRes?.success === false) {
                  this._connMessageService.showToastMessage('Customer status could not be changes.', 'error');
                  return;
                }

                this.syncActiveItemStatus(displayStatus);
                this.initialStatusValue = !!this.statusValue.value;
                this.isStatusDirty = false;
                this.statusReason = '';
                this._connMessageService.showToastMessage('Customer Status changes successfully', 'success');
              },
              error: () => {
                this.isSavingStatus = false;
                this._connMessageService.showToastMessage('Customer status could not be changes.', 'error');
              }
            });
        })
      },
      // reject: () => { }
    });







  }
  private resolveInitialStatusValue(): boolean {
    const activeItem = this._bizzCustomerService?.activeItem || {};
    const displayStatus = String(
      activeItem?.accountStatusDisplay ??
      activeItem?.AccountStatusDisplay ??
      activeItem?.accountStatusString ??
      ''
    ).trim().toLowerCase();
    const numericStatus = Number(activeItem?.accountStatus);

    if (
      displayStatus === 'inactive'
      || displayStatus === 'deactivated'
      || displayStatus === 'deactivated account'
      || displayStatus === 'accountexpired'
      || displayStatus === 'account expired'
      || numericStatus === 4
    ) {
      return false;
    }

    return true;
  }

  private syncActiveItemStatus(displayStatus: 'Active' | 'Inactive'): void {
    const activeItem = this._bizzCustomerService?.activeItem;
    if (!activeItem) {
      return;
    }

    activeItem.accountStatusDisplay = displayStatus;
    activeItem.AccountStatusDisplay = displayStatus;
    activeItem.accountStatusString = displayStatus;
    activeItem.status = displayStatus === 'Active';
  }

  onCurrencyChange(value: string): void {
    if (!this.showCurrencyDropdown) return;
    if (!value) return;
    this.selectedCurrency = value;
    this.isCurrencyDirty = this.selectedCurrency !== this.initialCurrency;
  }

  saveCurrency(): void {
    if (!this.showCurrencyDropdown) return;
    if (!this.isCurrencyDirty) return;

    this._confirmationService.confirm({
      message: `Do you want to save currency as ${this.selectedCurrency}?`,
      header: 'Currency',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.saveSelectedCurrencyForCustomer();
      },
      reject: () => {
        this.selectedCurrency = this.initialCurrency;
        this.isCurrencyDirty = false;
      }
    });
  }

  onTimezoneChange(value: string): void {
    if (!value) return;
    this.selectedTimezone = value;
    this.isTimezoneDirty = this.selectedTimezone !== this.initialTimezone;
  }

  saveTimezone(): void {
    if (!this.isTimezoneDirty || !this.selectedTimezone) return;

    const option = this.selectedTimezoneOption;
    if (!option) {
      this._connMessageService.showToastMessage('Select a timezone from the list before saving.', 'error');
      return;
    }

    this._confirmationService.confirm({
      message: `Do you want to save timezone as ${option.label}?`,
      header: 'Timezone',
      icon: 'pi pi-info-circle',
      accept: () => {
        const businessName = this.getBusinessNameForCustomer();
        this.isSavingTimezone = true;
        this._businessSetupService.saveBusinessTimeZone(
          { tzName: option.value, utcOffset: option.utcOffset, countryCode: option.countryCode },
          businessName
        ).subscribe({
          next: (res) => {
            this.isSavingTimezone = false;
            if (res?.success === false) {
              this._connMessageService.showToastMessage(res?.message || 'Timezone could not be saved.', 'error');
              return;
            }

            // Response echoes the business's full zone list with isActive flags — trust that over
            // just assuming the option we sent is now the active one.
            const confirmedActive = this.findActiveTimezone(Array.isArray(res?.data) ? res.data : []);
            const nextTimezone = confirmedActive || option.value;

            this.selectedTimezone = nextTimezone;
            this.initialTimezone = nextTimezone;
            this.isTimezoneDirty = false;
            this._connMessageService.showToastMessage(res?.message || 'Timezone saved.', 'success');
          },
          error: () => {
            this.isSavingTimezone = false;
            this._connMessageService.showToastMessage('Timezone could not be saved.', 'error');
          }
        });
      },
      reject: () => {
        this.selectedTimezone = this.initialTimezone;
        this.isTimezoneDirty = false;
      }
    });
  }

  private loadCurrencyOptions(): void {
    this._currencySettings.getCurrencyList().subscribe((list) => {
      if (!Array.isArray(list) || !list.length) return;

      const normalized = list
        .filter((item) => String(item?.code || '').trim().length > 0)
        .map((item) => ({
          label: item.label,
          value: String(item.code).toUpperCase(),
          name: item?.name,
          symbol: item?.symbol,
          raw: item?.raw
        }));

      if (!normalized.length) return;
      this.currencyOptions = normalized;

      if (!this.currencyOptions.some((option) => option.value === this.selectedCurrency)) {
        const usdOption = this.currencyOptions.find((option) => option.value === 'USD');
        this.selectedCurrency = usdOption?.value || this.currencyOptions[0].value || 'USD';
        this.initialCurrency = this.selectedCurrency;
        this.isCurrencyDirty = false;
      }
    });
  }

  private getCurrencyCodeFromCustomerDetail(): string {
    const activeItem = this._bizzCustomerService?.activeItem || {};
    const currencyList = Array.isArray(activeItem?.currencyList)
      ? activeItem.currencyList
      : [];
    const firstCurrency = currencyList.length > 0 ? currencyList[0] : null;
    const code = String(
      firstCurrency?.code ??
      firstCurrency?.Code ??
      ''
    ).trim().toUpperCase();

    if (!code) return 'USD';
    return code;
  }

  private saveSelectedCurrencyForCustomer(): void {
    const activeItem = this._bizzCustomerService?.activeItem || {};
    const businessId =
      activeItem?.businessId ??
      activeItem?.currentSubscription?.businessId ??
      activeItem?.businessSetup?.businessId ??
      null;
    const userId =
      activeItem?.userId ??
      activeItem?.currentSubscription?.userId ??
      null;
    const selectedCurrencyObj =
      this.currencyOptions.find((option) => option.value === this.selectedCurrency) || null;

    const payload = {
      businessId,
      userId,
      currency: selectedCurrencyObj
    };

    this.isSavingCurrency = true;
    this._bizzCustomerService.saveCustomerCurrencyPreference(payload).subscribe(
      (res: any) => {
        this.isSavingCurrency = false;
        if (res?.success) {
          this._currencySettings.setCurrencyPreference(this.selectedCurrency as any, false);
          this.initialCurrency = this.selectedCurrency;
          this.isCurrencyDirty = false;
          this._connMessageService.showToastMessage(`Currency saved as ${this.selectedCurrency}.`, 'success');
          return;
        }
        this.selectedCurrency = this.initialCurrency;
        this.isCurrencyDirty = false;
        this._connMessageService.showToastMessage('Currency could not be saved.', 'error');
      },
      () => {
        this.isSavingCurrency = false;
        this.selectedCurrency = this.initialCurrency;
        this.isCurrencyDirty = false;
        this._connMessageService.showToastMessage('Currency could not be saved.', 'error');
      }
    );
  }

  formatPhone(value: any): string {
    const digits = String(value ?? '').replace(/\D/g, '');
    if (!digits) {
      return '';
    }

    if (digits.length >= 10) {
      const normalized = digits.slice(-10);
      return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6, 10)}`;
    }

    return digits;
  }

  private parseJsonObject(value: any): any {
    if (typeof value !== 'string') {
      return value;
    }

    const trimmed = value.trim();
    if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
      return value;
    }

    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  private unwrapActiveItem(): any {
    let current = this.parseJsonObject(this._bizzCustomerService?.activeItem);
    const visited = new Set<any>();

    for (let depth = 0; depth < 7; depth += 1) {
      current = this.parseJsonObject(current);
      if (!current || typeof current !== 'object' || visited.has(current)) {
        break;
      }

      visited.add(current);

      if (Array.isArray(current)) {
        current = this.parseJsonObject(current[0]);
        continue;
      }

      const hasRenderableKeys = [
        current?.firstName,
        current?.FirstName,
        current?.lastName,
        current?.LastName,
        current?.businessName,
        current?.companyName,
        current?.email,
        current?.businessEmail,
        current?.phone,
        current?.businessPhone,
        current?.accountID,
        current?.currentSubscription,
      ].some((v) => String(v ?? '').trim().length > 0);

      if (hasRenderableKeys) {
        return current;
      }

      const next =
        current?.data ??
        current?.result ??
        current?.payload ??
        current?.item ??
        current?.customer ??
        current?.customerDetail ??
        current?.account ??
        current?.accountDetail;

      if (!next) {
        return current;
      }

      current = next;
    }

    return current || {};
  }

  private stripDomainSuffix(value: string): string {
    return String(value || '').replace(/lincsell$/i, '').trim();
  }

  get displayFirstName(): string {
    const item = this.unwrapActiveItem();
    const value = String(item?.firstName ?? item?.FirstName ?? item?.companyName ?? '').trim();
    return value || '-';
  }

  get displayLastName(): string {
    const item = this.unwrapActiveItem();
    const value = String(item?.lastName ?? item?.LastName ?? '').trim();
    return value || '-';
  }

  get displayBusinessName(): string {
    // Industry "0" means the business is not set up yet, so the name is not shown either
    if (this.displayIndustryName === '-' && this.isIndustryZero) {
      return '-';
    }
    const item = this.unwrapActiveItem();
    const value = String(
      item?.businessName ??
      item?.companyName ??
      this.stripDomainSuffix(item?.currentSubscription?.businessNameWithDomain) ??
      ''
    ).trim();

    return value || '-';
  }

  get displayAddress(): string {
    const item = this.unwrapActiveItem();
    const value = String(
      item?.address ?? item?.currentSubscription?.address ?? item?.businessSetup?.address ?? ''
    ).trim();
    return value || '-';
  }

  get displayCity(): string {
    const item = this.unwrapActiveItem();
    const value = String(
      item?.city ?? item?.currentSubscription?.city ?? item?.businessSetup?.city ?? ''
    ).trim();
    return value || '-';
  }

  get displayState(): string {
    const item = this.unwrapActiveItem();
    const value = String(
      item?.state ?? item?.currentSubscription?.state ?? item?.businessSetup?.state ?? ''
    ).trim();
    return value || '-';
  }

  get displayZip(): string {
    const item = this.unwrapActiveItem();
    const value = String(
      item?.zip ?? item?.currentSubscription?.zip ?? item?.businessSetup?.zip ?? ''
    ).trim();
    return value || '-';
  }

  get displayContactNumber(): string {
    const item = this.unwrapActiveItem();
    const raw = item?.businessPhone ?? item?.phone ?? item?.contactNumber ?? '';
    const formatted = this.formatPhone(raw);
    return formatted || '-';
  }

  get displayContactEmail(): string {
    const item = this.unwrapActiveItem();
    const value = String(item?.email ?? item?.businessEmail ?? item?.Email ?? '').trim();
    return value || '-';
  }

  get displayNoOfLocations(): number | string {
    const item = this.unwrapActiveItem();
    const direct = Number(item?.noOfLocations ?? item?.numberOfLocations ?? item?.locationCount ?? 0);
    if (Number.isFinite(direct) && direct > 0) {
      return direct;
    }

    const subscribedPackages = Array.isArray(item?.currentSubscription?.subscribedPackages)
      ? item.currentSubscription.subscribedPackages
      : [];
    const locationPackage = subscribedPackages.find((pkg: any) =>
      String(pkg?.title || '').toLowerCase().includes('location')
    );
    const qty = Number(locationPackage?.qty ?? 0);
    if (Number.isFinite(qty) && qty > 0) {
      return qty;
    }

    return 0;
  }

  get displayCreatedDate(): any {
    const item = this.unwrapActiveItem();
    return item?.createdDate ?? item?.currentSubscription?.createdDate ?? null;
  }

  get displayIndustryName(): string {
    const item = this.unwrapActiveItem();
    // Stored value can carry a suffix after "||" (e.g. "Restaurant/Cafe || you325") — show only the part before it.
    const raw = String(
      item?.industry ?? item?.Industry ?? item?.industryName ?? item?.IndustryName ?? item?.currentSubscription?.industry ?? item?.businessSetup?.industry ?? ''
    );
    const value = raw.split('||')[0].trim();
    // "0" means the business type has not been set up yet
    return value && value !== '0' ? value : '-';
  }

  get isIndustryZero(): boolean {
    const item = this.unwrapActiveItem();
    const raw = String(
      item?.industry ?? item?.Industry ?? item?.industryName ?? item?.IndustryName ?? item?.currentSubscription?.industry ?? item?.businessSetup?.industry ?? ''
    );
    return raw.split('||')[0].trim() === '0';
  }

  // TODO re-enable: set to true to show the Setup Progress panel
  readonly SETUP_PROGRESS_ENABLED = false;

  // Setup progress flags
  get productCreated(): boolean {
    const item = this.unwrapActiveItem();
    return item?.productExist === true || item?.ProductExist === true;
  }

  get storeSetupCompleted(): boolean {
    const item = this.unwrapActiveItem();
    return item?.onlineStoreInitiated === true || item?.OnlineStoreInitiated === true;
  }

  get stripeConnected(): boolean {
    const item = this.unwrapActiveItem();
    return item?.stripePaymentConnected === true || item?.StripePaymentConnected === true;
  }

  // ===== Store Content (admin view of the done-for-you store-setup submission) =====

  isStoreContentExpanded = true;

  get availSaveOffer(): boolean {
    const item = this.unwrapActiveItem();
    return item?.availSaveOffer === true || item?.AvailSaveOffer === true;
  }

  get storeContentFileUrl(): string {
    const item = this.unwrapActiveItem();
    return String(item?.storeContentFileUrl ?? item?.StoreContentFileUrl ?? '').trim();
  }

  get storeContentStatusRaw(): number {
    const item = this.unwrapActiveItem();
    const parsed = Number(item?.storeContentStatus ?? item?.StoreContentStatus ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  readonly storeContentStatusOptions = STORE_CONTENT_STATUS_OPTIONS;

  get storeContentCompletedDate(): any {
    const item = this.unwrapActiveItem();
    return item?.storeContentCompletedDate ?? item?.StoreContentCompletedDate ?? null;
  }

  get storeContentShowNotificationBar(): boolean {
    const item = this.unwrapActiveItem();
    return item?.storeContentShowNotificationBar === true || item?.StoreContentShowNotificationBar === true;
  }

  get storeContentStatusLabel(): string {
    return STORE_CONTENT_STATUS_LABELS[this.storeContentStatusRaw] ?? 'Pending from user';
  }

  isEditingStoreContentStatus = false;
  storeContentStatusDraft: number | null = null;
  isSavingStoreContentStatus = false;

  startEditingStoreContentStatus(): void {
    this.storeContentStatusDraft = this.storeContentStatusRaw;
    this.isEditingStoreContentStatus = true;
  }

  cancelEditingStoreContentStatus(): void {
    this.isEditingStoreContentStatus = false;
    this.storeContentStatusDraft = null;
  }

  confirmStoreContentStatusChange(): void {
    if (this.storeContentStatusDraft === null || this.storeContentStatusDraft === this.storeContentStatusRaw) {
      this.cancelEditingStoreContentStatus();
      return;
    }

    const newValue = this.storeContentStatusDraft;
    const label = this.storeContentStatusOptions.find((o) => o.value === newValue)?.label ?? '';

    this._confirmationService.confirm({
      message: `Change this customer's store content status to "${label}"?`,
      header: 'Store Content',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.isSavingStoreContentStatus = true;
        this.persistStoreContentUpdate({ status: newValue }).subscribe((success) => {
          this.isSavingStoreContentStatus = false;
          if (success) {
            this.isEditingStoreContentStatus = false;
            this.storeContentStatusDraft = null;
          }
        });
      },
    });
  }

  onToggleStoreContentNotificationBar(value: boolean): void {
    this.persistStoreContentUpdate({ showNotificationBar: value }).subscribe();
  }

  private persistStoreContentUpdate(payload: { status?: number; showNotificationBar?: boolean }): Observable<boolean> {
    const item = this.unwrapActiveItem();
    // Same resolution path as displayBusinessName — the nested currentSubscription field is the
    // reliable one for this customer's own business. Top-level businessNameWithDomain/BusinessNameWithDomain
    // are NOT used here: on the admin payload they can hold an unrelated value (observed resolving to the
    // logged-in admin's own business instead of the customer being viewed).
    const businessName = String(item?.currentSubscription?.businessNameWithDomain ?? '');
    const nextStatus = payload.status ?? this.storeContentStatusRaw;
    const nextShowNotificationBar = payload.showNotificationBar ?? this.storeContentShowNotificationBar;

    return this._businessSetupService
      .updateStoreContentStatus(
        { Status: nextStatus, ShowNotificationBar: nextShowNotificationBar, BusinessNameWithDomain: businessName },
        businessName
      )
      .pipe(
        map((res: any) => {
          const success = res?.success ?? res?.Success;
          if (success === false) {
            this._connMessageService.showToastMessage('Store content could not be updated.', 'error');
            return false;
          }

          // Prefer the server-computed record (it owns StoreContentCompletedDate/StartedDate) — fall
          // back to the values we sent if the response shape doesn't include it.
          const data = res?.data ?? res?.Data ?? {};
          const serverStatus = Number(data?.storeContentStatus ?? data?.StoreContentStatus ?? nextStatus);
          const serverShowNotificationBar =
            data?.storeContentShowNotificationBar ?? data?.StoreContentShowNotificationBar ?? nextShowNotificationBar;
          const serverCompletedDate =
            data?.storeContentCompletedDate ?? data?.StoreContentCompletedDate ??
            (nextStatus === 2 ? new Date().toISOString() : null);

          item.storeContentStatus = serverStatus;
          item.StoreContentStatus = serverStatus;
          item.storeContentShowNotificationBar = serverShowNotificationBar;
          item.StoreContentShowNotificationBar = serverShowNotificationBar;
          item.storeContentCompletedDate = serverCompletedDate;
          item.StoreContentCompletedDate = serverCompletedDate;

          this._connMessageService.showToastMessage('Store content updated.', 'success');
          return true;
        }),
        catchError(() => {
          this._connMessageService.showToastMessage('Store content could not be updated.', 'error');
          return of(false);
        })
      );
  }

  // ===== Online Store Setup (admin view of the merchant's WebStore template creation) =====

  get onlineStoreInitiated(): boolean {
    const item = this.unwrapActiveItem();
    return item?.onlineStoreInitiated === true || item?.OnlineStoreInitiated === true;
  }

  get onlineStoreCreated(): boolean {
    const item = this.unwrapActiveItem();
    return item?.onlineStoreCreated === true || item?.OnlineStoreCreated === true;
  }

  get onlineStoreUrl(): string {
    const item = this.unwrapActiveItem();
    return String(item?.onlineStoreUrl ?? item?.OnlineStoreUrl ?? '').trim();
  }

  get onlineStoreName(): string {
    const item = this.unwrapActiveItem();
    return String(item?.onlineStoreName ?? item?.OnlineStoreName ?? '').trim();
  }

  get onlineStoreCreatedDate(): any {
    const item = this.unwrapActiveItem();
    return item?.onlineStoreCreatedDate ?? item?.OnlineStoreCreatedDate ?? null;
  }

  get onlineStoreTemplateId(): string {
    const item = this.unwrapActiveItem();
    const value = item?.onlineStoreTemplateId ?? item?.OnlineStoreTemplateId ?? null;
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  get selectedTemplateInfo(): { name: string; image: string } | null {
    const raw = Number(this.onlineStoreTemplateId);
    return Number.isFinite(raw) ? ONLINE_STORE_TEMPLATES[raw] ?? null : null;
  }

  get onlineStoreSubdomain(): string {
    const item = this.unwrapActiveItem();
    const value = item?.subdomain ?? item?.Subdomain
      ?? item?.requestedSubdomain ?? item?.RequestedSubdomain
      ?? item?.webStoreSubDomain ?? item?.WebStoreSubDomain
      ?? '';
    return String(value).trim();
  }

  // Save paths are inconsistent about which field they persist the merchant's brand color
  // selection under (patchSavedStore/saveDomain write brandColorSelections, not brandColors) — see
  // the matching comment in online-store-setup.component.ts's ngOnInit. Check every candidate, in
  // saved-value order, same as the merchant-facing page does.
  get brandColors(): string[] {
    const item = this.unwrapActiveItem();
    const candidates = [
      item?.brandColorSelections,
      item?.BrandColorSelections,
      item?.brandColors,
      item?.BrandColors,
      item?.brandColorDetails,
      item?.BrandColorDetails
    ];
    const value = candidates.find((candidate) => Array.isArray(candidate) && candidate.length > 0) ?? [];
    return value
      .map((color: any) => typeof color === 'string' ? color : (color?.code ?? color?.Code))
      .map((color: any) => String(color).trim().toLowerCase())
      .filter((color: string) => !!color);
  }

  getBrandColorInfo(code: string): { name: string; hex: string } {
    const key = String(code || '').trim().toLowerCase();
    return BRAND_COLOR_OPTIONS[key] ?? { name: code, hex: '#6B7280' };
  }

  getBrandColorTextColor(code: string): string {
    const hex = this.getBrandColorInfo(code).hex.replace('#', '');
    if (hex.length !== 6) return '#101828';

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    const luminance = (red * 299 + green * 587 + blue * 114) / 1000;
    return luminance > 160 ? '#101828' : '#ffffff';
  }

  get customOnlineStoreRequested(): boolean {
    const item = this.unwrapActiveItem();
    return item?.customOnlineStoreRequested === true || item?.CustomOnlineStoreRequested === true;
  }

  get customOnlineStoreRequestedDate(): any {
    const item = this.unwrapActiveItem();
    return item?.customOnlineStoreRequestedDate ?? item?.CustomOnlineStoreRequestedDate ?? null;
  }

  get templateSetupStartedDate(): any {
    const item = this.unwrapActiveItem();
    return item?.templateSetupStartedDate ?? item?.TemplateSetupStartedDate ?? null;
  }

  readonly onlineStoreSetupStatusOptions: { label: string; value: OnlineStoreSetupStatus }[] = [
    { label: 'Pending', value: 'Pending' },
    { label: 'In Progress', value: 'InProgress' },
    { label: 'Completed', value: 'Completed' },
  ];

  get onlineStoreSetupStatusRaw(): OnlineStoreSetupStatus {
    const item = this.unwrapActiveItem();
    const value = String(item?.onlineStoreSetupStatus ?? item?.OnlineStoreSetupStatus ?? '').trim();
    return this.onlineStoreSetupStatusOptions.some((o) => o.value === value)
      ? (value as OnlineStoreSetupStatus)
      : 'Pending';
  }

  get onlineStoreSetupStatusLabel(): string {
    return this.onlineStoreSetupStatusOptions.find((o) => o.value === this.onlineStoreSetupStatusRaw)?.label
      ?? 'Pending';
  }

  get isTemplateSetupCompleted(): boolean {
    const item = this.unwrapActiveItem();
    return item?.templateSetupCompleted === true || item?.TemplateSetupCompleted === true;
  }

  get templateSetupCompletedDate(): any {
    const item = this.unwrapActiveItem();
    return item?.templateSetupCompletedDate ?? item?.TemplateSetupCompletedDate ?? null;
  }

  isEditingOnlineStoreSetupStatus = false;
  onlineStoreSetupStatusDraft: OnlineStoreSetupStatus | null = null;
  isSavingOnlineStoreSetupStatus = false;

  startEditingOnlineStoreSetupStatus(): void {
    this.onlineStoreSetupStatusDraft = this.onlineStoreSetupStatusRaw;
    this.isEditingOnlineStoreSetupStatus = true;
  }

  cancelEditingOnlineStoreSetupStatus(): void {
    this.isEditingOnlineStoreSetupStatus = false;
    this.onlineStoreSetupStatusDraft = null;
  }

  confirmOnlineStoreSetupStatusChange(): void {
    if (!this.onlineStoreSetupStatusDraft || this.onlineStoreSetupStatusDraft === this.onlineStoreSetupStatusRaw) {
      this.cancelEditingOnlineStoreSetupStatus();
      return;
    }

    const newValue = this.onlineStoreSetupStatusDraft;
    const label = this.onlineStoreSetupStatusOptions.find((o) => o.value === newValue)?.label ?? newValue;

    this._confirmationService.confirm({
      message: `Change this customer's online store setup status to "${label}"?`,
      header: 'Online Store Setup',
      icon: 'pi pi-info-circle',
      accept: () => {
        const item = this.unwrapActiveItem();
        const userId = item?.userId ?? item?.currentSubscription?.userId ?? item?.userID ?? null;
        const businessId =
          item?.businessId ?? item?.currentSubscription?.businessId ?? item?.businessSetup?.businessId ?? null;

        this.isSavingOnlineStoreSetupStatus = true;
        this._businessSetupService.updateOnlineStoreSetupStatus(newValue, userId, businessId).subscribe({
          next: (res: any) => {
            this.isSavingOnlineStoreSetupStatus = false;
            const success = res?.success ?? res?.Success;
            if (success === false) {
              const message = res?.message ?? res?.Message ?? 'Online store setup status could not be updated.';
              this._connMessageService.showToastMessage(message, 'error');
              return;
            }

            item.onlineStoreSetupStatus = newValue;
            item.OnlineStoreSetupStatus = newValue;

            // TemplateSetupCompleted is a one-way flag on the backend (never reset back to false) —
            // only sent, and only advanced locally, when the new status is 'Completed'. Any other
            // status change leaves the existing flag/date on `item` untouched.
            if (newValue === 'Completed') {
              const data = res?.data ?? res?.Data ?? {};
              const completedDate =
                data?.templateSetupCompletedDate ?? data?.TemplateSetupCompletedDate ?? new Date().toISOString();
              item.templateSetupCompleted = true;
              item.TemplateSetupCompleted = true;
              item.templateSetupCompletedDate = completedDate;
              item.TemplateSetupCompletedDate = completedDate;
            }

            this._connMessageService.showToastMessage('Online store setup status updated.', 'success');
            this.isEditingOnlineStoreSetupStatus = false;
            this.onlineStoreSetupStatusDraft = null;
          },
          error: () => {
            this.isSavingOnlineStoreSetupStatus = false;
            this._connMessageService.showToastMessage('Online store setup status could not be updated.', 'error');
          },
        });
      },
    });
  }

}
